import React, { useState } from "react";

import type ComplexPromotionModel from "@admin/domain/complexPromotions/models/ComplexPromotionModel";
import { useIsInvalid } from "@admin/components/FormErrorsContext";
import Input from "@admin/components/Input";
import TextArea from "@admin/components/TextArea";
import DateTimeInput from "@admin/components/DateTimeInput";
import CheckBox from "@admin/components/CheckBox";
import MultiLanguages from "@admin/components/MultiLanguage";
import Select from "@admin/components/Select";

type Props = {
    type: `amount` | `content` | `promocode`;
    promotion: ComplexPromotionModel | null;
};

const Common: React.FC<Props> = ({ type, promotion }) => {
    const [selectedDistributorRanks, setSelectedDistributorRanks] = useState(
        (promotion?.distributor_ranks || []).map((rank) => String(rank)),
    );

    return (
        <>
            <input type="hidden" name="type" value={type} />

            <MultiLanguages
                hasErrors={(language) => useIsInvalid(`texts[${language}]`)}
                languageElement={(language) => (
                    <>
                        <Input
                            label="Название <i>(для замены названий товаров на ссылки, введите название товара в виде :product_id_{uuid товара})</i>"
                            defaultValue={promotion?.texts[language]?.title}
                            name={`texts[${language}][title]`}
                        />

                        <TextArea
                            label="Описание"
                            defaultValue={
                                promotion?.texts[language]?.description
                            }
                            name={`texts[${language}][description]`}
                        />
                        {type === `amount` && (
                            <>
                                <Input
                                    label="Заголовок блока выбора товара в корзине"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.gift_selection_title ?? ``
                                    }
                                    name={`texts[${language}][gift_selection_title]`}
                                    placeholder="Спец. предложение по Новогодней акции"
                                    maxLength={255}
                                />
                                <TextArea
                                    label="Текст под заголовком блока выбора товара"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.gift_selection_description ?? ``
                                    }
                                    name={`texts[${language}][gift_selection_description]`}
                                    placeholder="Условия акции выполнены. Выберите один из продуктов по спец. цене"
                                    maxLength={2000}
                                />
                                <p className="text-muted mb-5">
                                    {`Для номера уровня в подписях ниже используйте {level}. Например: «Показать товары по спец. цене {level}-го уровня». В тексте прогресса двоеточие и оставшаяся сумма добавляются автоматически.`}
                                </p>
                                <Input
                                    label="Текст прогресса до следующего уровня"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.progress_level_template ?? ``
                                    }
                                    name={`texts[${language}][progress_level_template]`}
                                    placeholder="До товара по спец. цене {level}-го уровня"
                                    maxLength={255}
                                />
                                <Input
                                    label="Подпись стрелки к следующему уровню"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.next_level_template ?? ``
                                    }
                                    name={`texts[${language}][next_level_template]`}
                                    placeholder="Показать товары по спец. цене {level}-го уровня"
                                    maxLength={255}
                                />
                                <Input
                                    label="Подпись стрелки возврата к доступному уровню"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.previous_level_template ?? ``
                                    }
                                    name={`texts[${language}][previous_level_template]`}
                                    placeholder="Вернуться к товарам по спец. цене {level}-го уровня"
                                    maxLength={255}
                                />
                                <Input
                                    label="Текст достижения максимального уровня"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.maximum_level_text ?? ``
                                    }
                                    name={`texts[${language}][maximum_level_text]`}
                                    placeholder="Максимальный уровень достигнут"
                                    maxLength={255}
                                />
                                <Input
                                    label="Текст недоступного уровня товаров"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.unavailable_level_text ?? ``
                                    }
                                    name={`texts[${language}][unavailable_level_text]`}
                                    placeholder="Товары по спец. цене этого уровня пока недоступны."
                                    maxLength={255}
                                />
                                <Input
                                    label="Заголовок списка товаров на выбор"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.gift_selection_label_template ??
                                        ``
                                    }
                                    name={`texts[${language}][gift_selection_label_template]`}
                                    placeholder="Выбор товара по спец. цене"
                                    maxLength={255}
                                />
                                <Input
                                    label="Подпись выбранного товара в свёрнутом блоке"
                                    defaultValue={
                                        promotion?.texts[language]
                                            ?.selected_product_template ?? ``
                                    }
                                    name={`texts[${language}][selected_product_template]`}
                                    placeholder="Выбран товар {product}"
                                    maxLength={255}
                                />
                                <p className="text-muted mb-5">
                                    {`В этих двух полях {product} заменяется названием выбранного товара. Пока товар не выбран, подстановка в заголовке пустая, а подпись выбранного товара не показывается.`}
                                </p>
                                <p className="text-muted mb-5">
                                    Эти тексты показываются в корзине для
                                    товаров на выбор по акции. Если поле пустое,
                                    используется текст из подсказки.
                                </p>
                            </>
                        )}
                    </>
                )}
            />

            <DateTimeInput
                label="Начало акции"
                name="active_from"
                defaultValue={promotion?.active_from || ``}
            />

            <DateTimeInput
                label="Конец акции"
                name="active_to"
                defaultValue={promotion?.active_to || ``}
            />

            <CheckBox
                name="only_for_site"
                label="Применять только для сайта"
                defaultChecked={promotion ? promotion.only_for_site : true}
            />

            <CheckBox
                label="Разрешить добавлять несколько копий вознаграждения"
                name="use_multiplier"
                defaultChecked={promotion ? promotion.use_multiplier : true}
            />

            <div className="mb-5">
                <Select
                    multiple
                    prependEmptyOption={false}
                    label="Квалификации дистрибьюторов"
                    name="distributor_ranks[]"
                    size={window.CMS.distributorRanks?.length || undefined}
                    className={{ block: `mb-2` }}
                    options={(window.CMS.distributorRanks || []).map(
                        ({ rank, name }) => ({
                            value: rank,
                            label: name,
                        }),
                    )}
                    value={selectedDistributorRanks}
                    onChange={(event) => {
                        setSelectedDistributorRanks(
                            Array.from(event.target.selectedOptions).map(
                                (option) => option.value,
                            ),
                        );
                    }}
                />

                {selectedDistributorRanks.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-light text-dark"
                        onClick={() => setSelectedDistributorRanks([])}
                    >
                        Очистить
                    </button>
                )}
            </div>

            <input
                type="hidden"
                name="max_reward_quantity"
                value={promotion?.max_reward_quantity || ``}
            />

            {type === `content` || type === `promocode` ? (
                <CheckBox
                    name="insert_products_automatically"
                    label="Автоматически добавлять товары в корзину"
                    defaultChecked={
                        promotion
                            ? promotion.insert_products_automatically
                            : true
                    }
                />
            ) : (
                <input
                    type="hidden"
                    name="insert_products_automatically"
                    value="0"
                />
            )}
        </>
    );
};

export default Common;
